import {createContext, useState} from 'react';
import {Config} from '@stripe-internal/conversations-shared-dependencies';
import {parseConfigFromPage} from '../helpers/parseConfigFromPage';
import {useMountEffect} from '../useMountEffect';
import {configureSentry} from '../helpers/sentry';
import {defaultProfile} from './ProfileProvider/ProfileProvider';

export const defaultConfig: Config = {
  profile: defaultProfile,
  parentOrigin: '',
  stripeThirdPartyBaseUrl: '',
  audience: 'express',
  entrypoint: 'express_support_site',
  featureFlags: {
    enableUniversalHelp: false,
    universalHelpUseNewDesign: false,
    universalHelpUseDotComDesign: false,
    enableSupportConversationsEnforceAuth: false,
    universalHelpDotComFeedbackButtons: false,
    universalHelpDotComNoLlm: false,
    universalHelpDotComCsf: false,
    universalHelpEncourageMoreDetailFromUserMse5042: false,
    enableScreenshareUi: false,
    enableScreenshareContextProvider: false,
    universalHelpUseInlineRacEmailForm: false,
    useExtractedConversationsAssistant: false,
    enableRegionHeaderRouting: false,
    enableConversationsApiV2: false,
    enableConversationsThreads: false,
    enablePaidSupportAssistantCriticalIssueReporting: false,
    enableConversationsThreadsHistory: false,
    enableUhPendingInput: false,
    enableLiveChatSupportInMobile: false,
    enableExpressInactivityTimeout: false,
    enableExpressInactivityTimeoutSupportSite: false,
  },
  experiments: {
    supportFrontendTestExperiment: {
      variant: 'control',
      experimentName: 'support_frontend_text_experiment',
    },
  },
  revision: '',
  publicSentryDsn: '',
  urls: {
    supportSite: '',
    uploadsHost: '',
    attachmentDownloadPath: '',
  },
  surfaceLoadId: '1234',
  token: '',
  livemode: false,
  visitor: {
    id: 'invalid-visitor',
    sessionType: 'anonymous',
    emailConfirmed: false,
  },
  resumeVisibilityState: 'open',
};

export const ConfigContext = createContext<Config>(defaultConfig);

interface Props {
  children: JSX.Element;
}

export function ConfigProvider({children}: Props) {
  const [config] = useState<Config>(
    () => parseConfigFromPage() || defaultConfig,
  );

  const {visitor, revision, publicSentryDsn} = config;

  useMountEffect(() => {
    if (publicSentryDsn) {
      configureSentry(publicSentryDsn, revision, visitor);
    }
  });

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}
